import { Link } from "react-router-dom";
import { Mail, Globe, Phone } from "lucide-react";
import { useI18n, LANGUAGES } from "@/lib/i18n";
import { IMAGES } from "@/lib/images";
import { WhatsAppIcon, WHATSAPP_URL } from "./WhatsAppButton";
import { Reveal } from "./Reveal";

export const Footer = () => {
  const { t, changeLang, lang } = useI18n();
  return (
    <footer id="contact" className="border-t border-[var(--elt-line)]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-24 pb-10">
        <Reveal>
          <div className="text-center mb-20">
            <img src={IMAGES.logo} alt="Europe Luxury Transfers" className="h-32 w-auto mx-auto mb-8" />
            <p className="font-serif-elt text-2xl md:text-3xl font-light tracking-[0.12em] text-[var(--elt-gold-light)]">{t("footer.signature")}</p>
            <p className="mt-4 text-[0.7rem] tracking-[0.35em] uppercase text-[var(--elt-muted)]">{t("footer.tagline")}</p>
            <Link to="/request" data-testid="footer-request-journey-btn" className="btn-gold mt-10">{t("cta.request")}</Link>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-12 pb-16 border-b border-white/8">
          <div>
            <p className="eyebrow mb-5">{t("footer.contact")}</p>
            <a href="mailto:info@europeluxurytransfers.com" data-testid="footer-email-link" className="flex items-center gap-3 text-sm font-light text-[var(--elt-ivory)]/80 hover:text-[var(--elt-gold-light)] transition-colors">
              <Mail size={14} className="text-[var(--elt-gold)]" /> info@europeluxurytransfers.com
            </a>
            <a href="tel:+33751514276" data-testid="footer-phone-link" className="mt-3 flex items-center gap-3 text-sm font-light text-[var(--elt-ivory)]/80 hover:text-[var(--elt-gold-light)] transition-colors">
              <Phone size={14} className="text-[var(--elt-gold)]" /> +33 7 51 51 42 76
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="mt-3 flex items-center gap-3 text-sm font-light text-[var(--elt-ivory)]/80 hover:text-[var(--elt-gold-light)] transition-colors">
              <span className="text-[var(--elt-gold)]"><WhatsAppIcon size={15} /></span> WhatsApp
            </a>
            <a href="https://www.europeluxurytransfers.com" data-testid="footer-website-link" target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-3 text-sm font-light text-[var(--elt-ivory)]/80 hover:text-[var(--elt-gold-light)] transition-colors">
              <Globe size={14} className="text-[var(--elt-gold)]" /> www.europeluxurytransfers.com
            </a>
            <p className="mt-5 text-[0.65rem] tracking-[0.28em] uppercase text-[var(--elt-gold)]">{t("misc.available247")}</p>
          </div>
          <div>
            <p className="eyebrow mb-5">{t("footer.navigation")}</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2">
              {["home", "services", "fleet", "experience", "corporate", "partners", "destinations", "about"].map((k) => (
                <Link key={k} to={`/#${k === "home" ? "hero" : k}`} className="text-sm font-light text-[var(--elt-muted)] hover:text-[var(--elt-ivory)] transition-colors">{t(`nav.${k}`)}</Link>
              ))}
            </div>
          </div>
          <div>
            <p className="eyebrow mb-5">{t("footer.languages")}</p>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => (
                <button key={l.code} data-testid={`footer-lang-${l.code}`} onClick={() => changeLang(l.code)}
                  className={`text-[0.62rem] tracking-[0.2em] px-3 py-1.5 border transition-colors ${lang === l.code ? "border-[var(--elt-gold)] text-[var(--elt-gold-light)]" : "border-white/10 text-[var(--elt-muted)] hover:border-white/30"}`}>
                  {l.short}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[0.62rem] tracking-[0.25em] uppercase text-[var(--elt-muted)]">© {new Date().getFullYear()} Europe Luxury Transfers. {t("footer.rights")}</p>
          <p className="font-serif-elt italic text-lg text-[var(--elt-gold)]/70">{t("footer.tagline")}</p>
        </div>
      </div>
    </footer>
  );
};
